import type { StateCreator } from 'zustand';
import type { AppState } from '../types';
import type { UnitSystem } from '@/lib/units';
import type { ViewMode, DragState } from '@/types';
import type { WeeklyCheckinPreferences } from '@/types/weekly-checkin';
import { DEFAULT_WEEKLY_CHECKIN_PREFERENCES } from '@/types/weekly-checkin';

export interface UISlice {
    preferences: {
        proMode: boolean;
        darkMode: boolean;
        unitSystem: UnitSystem;
        weekStart: 'Monday' | 'Sunday';
        timezone: string;
        dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
        timeFormat: '12h' | '24h';
        weeklyCheckIn: WeeklyCheckinPreferences;
        showEducation?: boolean;
        hasSeenWalkthrough?: boolean;
        unplannedClickExternal?: boolean;
        thresholdAckSignature?: string;
    };
    viewMode: ViewMode;
    selectedWeek: number;
    dragState: DragState | null;
    isLoading: boolean;
    isHydrating: boolean;
    lifeStatus: 'fresh' | 'busy' | 'smashed' | 'sick';
    stressLevel: number;
    isGuestOnboardingPlan: boolean;

    stats: {
        workoutsWatched: number;
        screensVisited: number;
    };

    setViewMode: (mode: ViewMode) => void;
    setSelectedWeek: (week: number) => void;
    setDragState: (state: DragState | null) => void;
    setLoading: (loading: boolean) => void;
    setHydrating: (hydrating: boolean) => void;
    setLifeStatus: (status: 'fresh' | 'busy' | 'smashed' | 'sick') => void;
    setStressLevel: (level: number) => void;
    setPreferences: (prefs: Partial<UISlice['preferences']>) => void;

    consent: {
        essential: boolean;
        analytics: boolean;
        health: boolean;
        dismissed: boolean;
    };
    setConsent: (consent: Partial<UISlice['consent']>) => void;

    incrementWorkoutsWatched: () => void;
    incrementScreensVisited: () => void;

    showCookiePrefs: boolean;
    setShowCookiePrefs: (show: boolean) => void;

    // Pending Sync (warning dialog before overwriting data)
    pendingSync: AppState['pendingSync'];
    setPendingSync: (pending: AppState['pendingSync']) => void;
    clearPendingSync: () => void;
    applyPendingSync: () => void;

    // Merge Conflict
    mergeConflict: AppState['mergeConflict'];
    setMergeConflict: (conflict: AppState['mergeConflict']) => void;

    // Guest Onboarding
    setGuestOnboardingPlan: (value: boolean) => void;
}

export const createUISlice: StateCreator<AppState, [], [], UISlice> = (set) => ({
    preferences: {
        proMode: false,
        darkMode: false,
        unitSystem: 'metric' as UnitSystem,
        weekStart: 'Monday' as const,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        dateFormat: 'DD/MM/YYYY' as const,
        timeFormat: '24h' as const,
        weeklyCheckIn: { ...DEFAULT_WEEKLY_CHECKIN_PREFERENCES },
        showEducation: true,
        hasSeenWalkthrough: false,
    },
    viewMode: 'scroll' as ViewMode,
    selectedWeek: 1,
    dragState: null,
    isLoading: false,
    isHydrating: false,
    lifeStatus: 'fresh',
    stressLevel: 0,
    isGuestOnboardingPlan: false,

    setViewMode: (viewMode) => set({ viewMode }),
    setSelectedWeek: (selectedWeek) => set({ selectedWeek }),
    setDragState: (dragState) => set({ dragState }),
    setLoading: (isLoading) => set({ isLoading }),
    setHydrating: (isHydrating) => set({ isHydrating }),
    setLifeStatus: (lifeStatus) => set({ lifeStatus }),
    setStressLevel: (stressLevel) => set({ stressLevel }),

    setPreferences: (prefs) => set((state) => ({
        preferences: { ...state.preferences, ...prefs }
    })),

    consent: {
        essential: true, // Always true if they use the app
        analytics: false,
        health: false,
        dismissed: false,
    },
    setConsent: (consent) => set((state) => ({
        consent: { ...state.consent, ...consent }
    })),

    stats: {
        workoutsWatched: 0,
        screensVisited: 0,
    },

    incrementWorkoutsWatched: () => set((state) => ({
        stats: { ...state.stats, workoutsWatched: state.stats.workoutsWatched + 1 }
    })),

    incrementScreensVisited: () => set((state) => ({
        stats: { ...state.stats, screensVisited: state.stats.screensVisited + 1 }
    })),



    showCookiePrefs: false,
    setShowCookiePrefs: (show) => set({ showCookiePrefs: show }),

    // Pending Sync State
    pendingSync: {
        active: false,
        source: '',
        changes: [],
        applyFn: null,
    },
    setPendingSync: (pending) => set({ pendingSync: pending }),
    clearPendingSync: () => set({
        pendingSync: { active: false, source: '', changes: [], applyFn: null },
    }),
    applyPendingSync: () => {
        const state = set((s) => {
            if (s.pendingSync.applyFn) {
                s.pendingSync.applyFn();
            }
            return {
                pendingSync: { active: false, source: '', changes: [], applyFn: null },
            };
        });
        return state;
    },

    // Merge Conflict State
    mergeConflict: {
        active: false,
        localSummary: '',
        cloudSummary: '',
        onKeepCloud: () => { },
        onKeepLocal: () => { },
    },
    setMergeConflict: (conflict) => set({ mergeConflict: conflict }),

    // Guest Onboarding
    setGuestOnboardingPlan: (value) => set({ isGuestOnboardingPlan: value }),
});
